/** biome-ignore-all lint/suspicious/noExplicitAny: expected */
/** biome-ignore-all lint/suspicious/noAssignInExpressions: expected */

import type { PreTransformerFunc } from "@ext/markdown/core/Parser/Transformer/preTransformTokens";
import { derivedToken } from "@ext/markdown/core/Parser/Transformer/tokenMap";
import { COL_MIN_WIDTH } from "@ext/markdown/elements/table/edit/model/nodes/customTable";
import type Token from "markdown-it/lib/token";

// Tags that make up a table. Written one per line they are tokenized as block tokens, but
// written several to a line (`<tr><td>a</td></tr>`) they land inside a paragraph's inline
// token instead. A table split across both forms cannot be transformed, so lift the tags of
// the inline half up to block level first and keep the whole table in one token stream.
const TABLE_TAGS = ["table", "tr", "td", "th"];

const isHoistableTableTag = (token: Token) =>
	(token.type === "tag_open" || token.type === "tag_close") && TABLE_TAGS.includes(token.meta?.tag);

const isBlank = (token: Token) => token.type === "softbreak" || (token.type === "text" && !token.content.trim());

const hoistInlineTableTags = (tokens: Token[]): Token[] => {
	const result: Token[] = [];

	for (let idx = 0; idx < tokens.length; idx++) {
		const token = tokens[idx];
		if (token.type !== "inline" || !token.children?.some(isHoistableTableTag)) {
			result.push(token);
			continue;
		}

		const inParagraph = tokens[idx - 1]?.type === "paragraph_open" && tokens[idx + 1]?.type === "paragraph_close";
		if (inParagraph) {
			result.pop();
			idx++;
		}

		let chunk: Token[] = [];
		const flushChunk = () => {
			const children = chunk;
			chunk = [];
			if (children.every(isBlank)) return;

			result.push({ type: "paragraph_open", tag: "p", nesting: 1 } as Token);
			result.push({ ...token, children, content: children.map((child) => child.content).join("") } as Token);
			result.push({ type: "paragraph_close", tag: "p", nesting: -1 } as Token);
		};

		for (const child of token.children) {
			if (!isHoistableTableTag(child)) {
				chunk.push(child);
				continue;
			}
			flushChunk();
			result.push(child);
		}
		flushChunk();
	}

	return result;
};

// A row holds cells and nothing else, but Markdown can put a block between them: content
// indented by four spaces after a blank line, for instance, is tokenized as a code block.
// Such a row cannot be built as a table, so keep the content and give it a cell of its own.
const wrapRowContentInCell = (tokens: Token[]): Token[] => {
	const result: Token[] = [];
	let inRow = false;
	let inCell = false;
	let orphans: Token[] = [];

	const flushOrphans = () => {
		const children = orphans;
		orphans = [];
		if (!children.length || children.every(isBlank)) return;

		result.push({
			type: "tag_open",
			tag: "",
			info: "td",
			nesting: 1,
			meta: { tag: "td", attributes: [] },
		} as Token);
		result.push(...children);
		result.push({ type: "tag_close", tag: "", info: "/td", nesting: -1, meta: { tag: "td" } } as Token);
	};

	for (const token of tokens) {
		const tagName = isHoistableTableTag(token) ? token.meta.tag : null;
		if (tagName) {
			flushOrphans();
			if (tagName === "table") inRow = inCell = false;
			if (tagName === "tr") {
				inRow = token.type === "tag_open";
				inCell = false;
			}
			if (tagName === "td" || tagName === "th") inCell = token.type === "tag_open";
			result.push(token);
			continue;
		}

		if (inRow && !inCell) {
			orphans.push(token);
			continue;
		}
		result.push(token);
	}
	flushOrphans();

	return result;
};

const tableTransform: PreTransformerFunc = ({ tokens: rawTokens, ...otherProps }) => {
	const tokens = wrapRowContentInCell(hoistInlineTableTags(rawTokens));
	let depth = -1;
	const tableStates: {
		isCell: boolean;
		isCellList: number;
		colwidths: string[];
		realCol: number;
		rowSpans: number[];
	}[] = [];

	const closeRow = () => {
		for (let i = 0; i < tableStates[depth].rowSpans.length; i++) {
			if (tableStates[depth].rowSpans[i] > 0) {
				tableStates[depth].rowSpans[i]--;
			}
		}
	};

	for (let idx = 0; idx < tokens.length; idx++) {
		const token = tokens[idx];
		if (token.type === "tag_open" && (token.info === "table" || token.info.startsWith("table "))) {
			depth++;
			tableStates[depth] = {
				isCell: false,
				isCellList: 0,
				colwidths: [],
				realCol: 0,
				rowSpans: [],
			};

			const tableOpen = derivedToken(token, {
				type: "table_open",
				tag: "table",
				nesting: 1,
				attrs: {},
			});
			if (token?.meta) tableOpen.meta = token.meta;
			(token?.meta?.attributes as [])?.forEach(({ name, value }) => {
				tableOpen.attrs[name] = value;
			});

			tokens.splice(idx, 1, tableOpen);
			if (
				tokens[idx + 1]?.type === "paragraph_open" &&
				tokens[idx + 2]?.type === "inline" &&
				tokens[idx + 3]?.type === "paragraph_close"
			) {
				const colgroup = tokens[idx + 2].children;
				if (colgroup[0]?.meta?.tag === "colgroup")
					for (let i = 1; i < colgroup.length; i++) {
						const currentToken = colgroup[i];
						if (currentToken.type === "tag" && currentToken.meta?.tag === "col") {
							const colwidth = currentToken.meta.attributes?.find((a) => a.name === "width")?.value;
							tableStates[depth].colwidths.push(colwidth);
						}
						if (currentToken.type === "tag_close" && currentToken.meta?.tag === "colgroup") {
							tokens.splice(
								idx + 1,
								3,
								derivedToken(token, { type: "tbody_open", tag: "tbody", nesting: 1 }),
							);
							break;
						}
					}
			} else {
				tokens.splice(idx + 1, 0, derivedToken(token, { type: "tbody_open", tag: "tbody", nesting: 1 }));
			}
			continue;
		}
		if (token.type === "tag_close" && token.info === "/table") {
			// A close with no open belongs to no table: dropping it keeps the rest of the
			// article readable, while closing one more level would run past tableStates.
			if (depth === -1) {
				tokens.splice(idx, 1);
				idx--;
				continue;
			}

			// One closing tag becomes two tokens; both keep the line it was written on.
			tokens.splice(
				idx,
				1,
				derivedToken(token, { type: "tbody_close", tag: "tbody", nesting: -1 }),
				derivedToken(token, { type: "table_close", tag: "table", nesting: -1 }),
			);
			depth--;
		}
		if (depth !== -1) {
			if (token.type === "hr") {
				if (tokens[idx + 1].type === "hr") {
					tokens.splice(idx, 0, derivedToken(token, { type: "tr_open", tag: "tr", nesting: 1 }));
					idx++;
					tokens.splice(idx, 0, derivedToken(token, { type: "tr_close", tag: "tr", nesting: -1 }));
					idx++;
					closeRow();
				}
				tokens.splice(idx, 1);
				idx--;
			}
			if (
				tableStates[depth].isCell &&
				(token.type === "bullet_list_open" || token.type === "ordered_list_open")
			) {
				tableStates[depth].isCellList++;
			}
			if (tableStates[depth].isCellList) {
				if (token.type === "bullet_list_close" || token.type === "ordered_list_close") {
					tableStates[depth].isCellList--;
				}
				continue;
			}
			if (token.type === "bullet_list_open" || (token.type === "tag_open" && token.meta.tag === "tr")) {
				tokens.splice(idx, 1, derivedToken(token, { type: "tr_open", tag: "tr", nesting: 1 }));
				tableStates[depth].realCol = 0;
			}
			if (token.type === "bullet_list_close" || (token.type === "tag_close" && token.meta.tag === "tr")) {
				tokens.splice(idx, 1, derivedToken(token, { type: "tr_close", tag: "tr", nesting: -1 }));
				closeRow();
			}
			if (token.type === "list_item_open" || (token.type === "tag_open" && token.meta.tag === "td")) {
				tableStates[depth].isCell = true;

				while (tableStates[depth].rowSpans[tableStates[depth].realCol] > 0) {
					tableStates[depth].realCol++;
				}

				const isAnnotation = tokens[idx + 1].type === "annotation";
				const attrs: Record<string, any> = {};
				if (isAnnotation)
					tokens[idx + 1].meta.attributes?.forEach((a) => {
						attrs[a.name] = a.value;
					});
				if (token.type === "tag_open")
					token.meta.attributes?.forEach((a) => {
						attrs[a.name] = a.value;
					});
				if (attrs.colspan && typeof attrs.colspan === "string") attrs.colspan = parseFloat(attrs.colspan);
				if (attrs.rowspan && typeof attrs.rowspan === "string") attrs.rowspan = parseFloat(attrs.rowspan);
				if (attrs.colwidth && !Array.isArray(attrs.colwidth)) attrs.colwidth = [attrs.colwidth];
				if (attrs.colwidth) attrs.colwidth = attrs.colwidth.map((c) => Math.max(parseFloat(c), COL_MIN_WIDTH));
				if (tableStates[depth].colwidths.length > 0) {
					const colspan = attrs.colspan ?? 1;
					const rowspan = attrs.rowspan ?? 1;

					const colwidth: number[] = [];
					for (let c = 0; c < colspan; c++) {
						const col = tableStates[depth].realCol + c;

						while (tableStates[depth].rowSpans.length <= col) {
							tableStates[depth].rowSpans.push(0);
						}

						const w = tableStates[depth].colwidths[col];
						// A column with no width of its own keeps its place in the list: the list is
						// read by position, so dropping it would move every width that follows onto
						// the wrong column.
						colwidth.push(w ? Math.max(parseFloat(String(w)), COL_MIN_WIDTH) : 0);
					}

					if (colwidth.some(Boolean)) {
						attrs.colwidth = colwidth;
					}

					if (rowspan > 1) {
						for (let c = 0; c < colspan; c++) {
							const col = tableStates[depth].realCol + c;
							while (tableStates[depth].rowSpans.length <= col) {
								tableStates[depth].rowSpans.push(0);
							}
							tableStates[depth].rowSpans[col] = rowspan;
						}
					}

					tableStates[depth].realCol += colspan;
				}
				tokens.splice(
					idx,
					isAnnotation ? 2 : 1,
					derivedToken(token, {
						type: "td_open",
						tag: "td",
						nesting: 1,
						attrs: attrs,
						meta: isAnnotation ? tokens[idx + 1].meta : null,
					}),
				);
			}
			if (token.type === "list_item_close" || (token.type === "tag_close" && token.meta.tag === "td")) {
				tableStates[depth].isCell = false;
				tokens.splice(idx, 1, derivedToken(token, { type: "td_close", tag: "td", nesting: -1 }));
			}
		}
		if (token?.children) token.children = tableTransform({ tokens: token.children, ...otherProps });
	}

	for (let idx = 0; idx < tokens.length; idx++) {
		if ((tokens[idx].type === "td_open" || tokens[idx].type === "th_open") && tokens[idx + 1].type === "inline") {
			const inline = tokens[idx + 1];
			tokens.splice(
				idx + 1,
				1,
				derivedToken(inline, { type: "paragraph_open", tag: "p", nesting: 1 }),
				inline,
				derivedToken(inline, { type: "paragraph_close", tag: "p", nesting: -1 }),
			);
		}
	}
	return tokens;
};

export default tableTransform;
